/**
 * Account Relationships
 *
 * Functions for linking/unlinking parents, teachers, and learners.
 * Part of the accounts domain (split from accountHelpers.ts).
 */

import { AccountsState, RelationshipGraph, isLearnerAccount, isParentAccount } from '../types';
import { error as logError } from '../../utils/logger';

/** Maximum number of parents that can be linked to a single learner */
export const MAX_PARENTS_PER_LEARNER = 5;

/**
 * Link parent to learner (bidirectional)
 * Enforces MAX_PARENTS_PER_LEARNER cap.
 * Returns unchanged state if cap is exceeded.
 */
export function linkParentToLearner(accountsState: AccountsState, parentId: string, learnerId: string): AccountsState {
  const parent = accountsState.accountsById[parentId];
  const learner = accountsState.accountsById[learnerId];
  if (!parent || !isParentAccount(parent)) {
    logError(`Invalid parent account: ${parentId}`);
    return accountsState;
  }
  if (!learner || !isLearnerAccount(learner)) {
    logError(`Invalid learner account: ${learnerId}`);
    return accountsState;
  }

  // Check if already linked (dedup)
  const existingParents = accountsState.relationships.learnerToParents[learnerId] || [];
  if (existingParents.includes(parentId)) {
    return accountsState; // Already linked, no-op
  }

  // Enforce max parents cap
  if (existingParents.length >= MAX_PARENTS_PER_LEARNER) {
    logError(`Cannot link parent to learner ${learnerId}: max ${MAX_PARENTS_PER_LEARNER} parents reached`);
    return accountsState;
  }
  const newRelationships: RelationshipGraph = {
    ...accountsState.relationships,
    parentToLearners: {
      ...accountsState.relationships.parentToLearners,
      [parentId]: [...(accountsState.relationships.parentToLearners[parentId] || []), learnerId].filter((id, index, self) => self.indexOf(id) === index)
    },
    learnerToParents: {
      ...accountsState.relationships.learnerToParents,
      [learnerId]: [...existingParents, parentId].filter((id, index, self) => self.indexOf(id) === index)
    }
  };
  return {
    ...accountsState,
    relationships: newRelationships
  };
}

/**
 * Link teacher to learner
 */
export function linkTeacherToLearner(accountsState: AccountsState, teacherId: string, learnerId: string): AccountsState {
  const teacher = accountsState.accountsById[teacherId];
  const learner = accountsState.accountsById[learnerId];
  if (!teacher || teacher.role !== 'teacher') {
    logError(`Invalid teacher account: ${teacherId}`);
    return accountsState;
  }
  if (!learner || !isLearnerAccount(learner)) {
    logError(`Invalid learner account: ${learnerId}`);
    return accountsState;
  }
  const newRelationships: RelationshipGraph = {
    ...accountsState.relationships,
    teacherToLearners: {
      ...accountsState.relationships.teacherToLearners,
      [teacherId]: [...(accountsState.relationships.teacherToLearners[teacherId] || []), learnerId].filter((id, index, self) => self.indexOf(id) === index) // Deduplicate
    },
    learnerToTeachers: {
      ...accountsState.relationships.learnerToTeachers,
      [learnerId]: [...(accountsState.relationships.learnerToTeachers?.[learnerId] || []), teacherId].filter((id, index, self) => self.indexOf(id) === index) // Deduplicate
    }
  };
  return {
    ...accountsState,
    relationships: newRelationships
  };
}

/**
 * Unlink a parent from a learner (bidirectional).
 * Pure function — returns new AccountsState.
 */
export function unlinkParentFromLearner(accountsState: AccountsState, parentId: string, learnerId: string): AccountsState {
  const rels = accountsState.relationships;
  return {
    ...accountsState,
    relationships: {
      ...rels,
      parentToLearners: {
        ...rels.parentToLearners,
        [parentId]: (rels.parentToLearners[parentId] || []).filter(id => id !== learnerId)
      },
      learnerToParents: {
        ...rels.learnerToParents,
        [learnerId]: (rels.learnerToParents[learnerId] || []).filter(id => id !== parentId)
      }
    }
  };
}

/**
 * Unlink a teacher from a learner.
 * Also removes the learner from all classrooms owned by this teacher
 * and cleans up the learnerToClassrooms reverse index.
 * Pure function — returns new AccountsState.
 */
export function unlinkTeacherFromLearner(accountsState: AccountsState, teacherId: string, learnerId: string): AccountsState {
  const rels = accountsState.relationships;

  // Remove learner from all classrooms owned by this teacher
  const updatedClassrooms = {
    ...accountsState.classrooms
  };
  const removedClassroomIds: string[] = [];
  for (const [classroomId, classroom] of Object.entries(updatedClassrooms)) {
    if (classroom.teacherId === teacherId && classroom.learnerIds.includes(learnerId)) {
      updatedClassrooms[classroomId] = {
        ...classroom,
        learnerIds: classroom.learnerIds.filter(id => id !== learnerId)
      };
      removedClassroomIds.push(classroomId);
    }
  }

  // Clean up learnerToClassrooms reverse index
  const currentL2C = rels.learnerToClassrooms || {};
  const learnerClassrooms = (currentL2C[learnerId] || []).filter(id => !removedClassroomIds.includes(id));

  // Add to archive
  const currentArchive = rels.archivedLearnerLinks || {};
  const teacherArchive = currentArchive[teacherId] || [];
  const newArchiveEntry = {
    learnerId,
    archivedAt: Date.now(),
    reason: 'unlink' as const,
    fromClassroomIds: removedClassroomIds
  };
  return {
    ...accountsState,
    classrooms: updatedClassrooms,
    relationships: {
      ...rels,
      teacherToLearners: {
        ...rels.teacherToLearners,
        [teacherId]: (rels.teacherToLearners[teacherId] || []).filter(id => id !== learnerId)
      },
      learnerToTeachers: {
        ...rels.learnerToTeachers,
        [learnerId]: (rels.learnerToTeachers?.[learnerId] || []).filter(id => id !== teacherId)
      },
      learnerToClassrooms: {
        ...currentL2C,
        [learnerId]: learnerClassrooms
      },
      archivedLearnerLinks: {
        ...currentArchive,
        [teacherId]: [...teacherArchive, newArchiveEntry]
      }
    }
  };
}

/**
 * Restore a soft-deleted learner link from the archive.
 * Re-links the teacher and learner, and optionally restores classroom membership.
 * Removes the entry from the archive.
 */
export function restoreArchivedLearner(accountsState: AccountsState, teacherId: string, learnerId: string, options?: {
  toClassroomId?: string;
}): AccountsState {
  const rels = accountsState.relationships;
  const currentArchive = rels.archivedLearnerLinks || {};
  const teacherArchive = currentArchive[teacherId] || [];

  // Find the archive entry
  const archiveEntry = teacherArchive.find(a => a.learnerId === learnerId);
  if (!archiveEntry) return accountsState;

  // Remove from archive
  const updatedTeacherArchive = teacherArchive.filter(a => a.learnerId !== learnerId);

  // 1. Re-link teacher and learner
  let nextState = linkTeacherToLearner(accountsState, teacherId, learnerId);

  // 2. Restore classroom membership if requested
  if (options?.toClassroomId) {
    // Lazy import to avoid cycle, or just inline the logic since we are in relationships.ts
    // Actually, we can just update the classroom directly here to avoid circular dependencies
    const classroom = nextState.classrooms[options.toClassroomId];
    if (classroom && !classroom.learnerIds.includes(learnerId)) {
      nextState = {
        ...nextState,
        classrooms: {
          ...nextState.classrooms,
          [options.toClassroomId]: {
            ...classroom,
            learnerIds: [...classroom.learnerIds, learnerId]
          }
        },
        relationships: {
          ...nextState.relationships,
          learnerToClassrooms: {
            ...nextState.relationships.learnerToClassrooms,
            [learnerId]: [...(nextState.relationships.learnerToClassrooms[learnerId] || []), options.toClassroomId]
          }
        }
      };
    }
  }

  // 3. Update archive state
  return {
    ...nextState,
    relationships: {
      ...nextState.relationships,
      archivedLearnerLinks: {
        ...currentArchive,
        [teacherId]: updatedTeacherArchive
      }
    }
  };
}